import { useState, useRef, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { DIAL_CODES, maskPhone, toE164 } from '../lib/phone'
import { humaniseSignInError, sendOutcome, RESEND_COOLDOWN_MS } from '../lib/signInErrors'
import { supportMailto } from '../lib/supportMail'
import { SupportText } from '../components/SupportText'
import {
  SIGN_IN_COPY,
  SIGN_IN_LANGS,
  SIGN_IN_LANG_KEY,
  dialLabel,
  pickSignInLang,
  type SignInLang,
} from '../lib/signInCopy'
import logo from '../assets/conetec-logo.svg'

type Stage = 'start' | 'code'
type Method = 'email' | 'phone'

/**
 * Passwordless sign-in, two stages on one screen, by email OR by text message.
 *
 * Invite-only is enforced in the database, not here: the migration 010 trigger
 * refuses any address — or, since 101, any phone number — that has no identity
 * row. This screen never ASKS the database "is this person known"; it repeats
 * what the send answered. From 25 Sep (ACC-39) it hid that answer behind a
 * conditional code step; SINCE 8 OCT (David) an unknown address is told it was
 * not found, with OneConetec support named — lib/signInErrors.ts's header has
 * the reasoning and what it gives up. A known person whose access is suspended
 * gets a code and is stopped after sign-in by App.tsx's `inactive` state.
 *
 * THE PHONE OPTION IS OFFERED ONLY WHEN `sign_in_options().sms` IS TRUE (102).
 * That flag is a COLUMN David flips once Twilio Verify is configured in the
 * Supabase dashboard; until then the button would lead to "unsupported phone
 * provider", which reads as the hub being broken (standing rule 4). An
 * UNDEFINED answer (loading, or the read failed) means NOT OFFERED — the same
 * posture as intentRouting's open-lane list: a door rendered optimistically
 * reaches nobody for as long as the fetch takes.
 *
 * ── ENGLISH OR SPANISH, BY THE BROWSER (SHL-25, 24 Sep) ─────────────────────
 *
 * There is no person yet, so the language comes from navigator.languages and
 * a visible switch, remembered in this browser only (lib/signInCopy explains
 * why asking the database would be B13's oracle). Every word on the screen,
 * including the errors and the phone option, comes from SIGN_IN_COPY — the
 * suite fails if an English sentence is typed back into this file.
 */
function readStoredLang(): string | null {
  try {
    return window.localStorage.getItem(SIGN_IN_LANG_KEY)
  } catch {
    return null
  }
}

function initialLang(): SignInLang {
  if (typeof navigator === 'undefined') return 'en'
  const langs = navigator.languages?.length ? navigator.languages : [navigator.language]
  return pickSignInLang(langs, readStoredLang())
}

export function SignIn() {
  const [lang, setLang] = useState<SignInLang>(initialLang)
  const t = SIGN_IN_COPY[lang]
  const [stage, setStage] = useState<Stage>('start')
  const [method, setMethod] = useState<Method>('email')
  const [smsOffered, setSmsOffered] = useState<boolean | undefined>(undefined)
  const [email, setEmail] = useState('')
  const [dial, setDial] = useState(DIAL_CODES[0].dial)
  const [phoneTyped, setPhoneTyped] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [resentAt, setResentAt] = useState<number | null>(null)
  const codeRef = useRef<HTMLInputElement>(null)

  const phone = toE164(phoneTyped, dial)

  useEffect(() => {
    if (stage === 'code') codeRef.current?.focus()
  }, [stage])

  useEffect(() => {
    const html = SIGN_IN_LANGS.find((l) => l.key === lang)?.htmlLang ?? 'en'
    document.documentElement.lang = html
    // Back to English when the screen goes away: the signed-in hub is English
    // whatever was chosen here, and a Spanish `lang` over English text makes a
    // screen reader read it with the wrong voice.
    return () => {
      document.documentElement.lang = 'en'
    }
  }, [lang])

  function chooseLang(next: SignInLang) {
    setLang(next)
    try {
      window.localStorage.setItem(SIGN_IN_LANG_KEY, next)
    } catch {
      // A private window or blocked storage: the switch still works for this
      // visit, it just is not remembered.
    }
  }

  useEffect(() => {
    let live = true
    supabase.rpc('sign_in_options').then(({ data, error }) => {
      if (!live) return
      setSmsOffered(!error && (data as { sms?: boolean } | null)?.sms === true)
    })
    return () => { live = false }
  }, [])

  async function sendCode(e?: React.FormEvent) {
    e?.preventDefault()
    setError(null)
    if (method === 'phone' && !phone) {
      setError(t.notAFullNumber)
      return
    }
    setBusy(true)
    // shouldCreateUser MUST be true, and this is the counter-intuitive part.
    // Identities exist WITHOUT auth users — first sign-in is exactly when the
    // auth user gets created, so refusing creation here would lock out every
    // invited person. Invite-only is the 010 trigger's job, not this flag's.
    const { error } =
      method === 'email'
        ? await supabase.auth.signInWithOtp({
            email: email.trim().toLowerCase(),
            options: { shouldCreateUser: true },
          })
        : await supabase.auth.signInWithOtp({
            phone: phone!,
            options: { shouldCreateUser: true, channel: 'sms' },
          })
    setBusy(false)
    // An unknown address comes back as an error from Supabase and is told it
    // was not found (David, 8 Oct). sendOutcome decides.
    const outcome = sendOutcome(error?.message, method, lang)
    if (outcome.kind === 'error') {
      setError(outcome.message)
      return
    }
    setStage('code')
    setResentAt(Date.now())
  }

  async function verify(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    const { error } =
      method === 'email'
        ? await supabase.auth.verifyOtp({
            email: email.trim().toLowerCase(),
            token: code.trim(),
            type: 'email',
          })
        : await supabase.auth.verifyOtp({ phone: phone!, token: code.trim(), type: 'sms' })
    setBusy(false)
    // The `?? ''` is unreachable at this stage.
    if (error) setError(humaniseSignInError(error.message, method, 'verify', lang) ?? '')
    // On success the onAuthStateChange listener in App takes over.
  }

  function switchMethod(next: Method) {
    setMethod(next)
    setError(null)
    setCode('')
    setStage('start')
  }

  const ready = method === 'email' ? !!email : !!phoneTyped

  return (
    <main className="signin">
      <form
        className="signin__card"
        onSubmit={stage === 'start' ? sendCode : verify}
        noValidate
      >
        <img className="signin__logo" src={logo} alt="Conetec" width={168} />

        <div className="signin__langs" role="group" aria-label={t.languageLabel}>
          {SIGN_IN_LANGS.map((l) => (
            <button
              key={l.key}
              type="button"
              lang={l.htmlLang}
              className="btn btn--link"
              aria-pressed={lang === l.key}
              onClick={() => chooseLang(l.key)}
              disabled={busy}
            >
              {l.label}
            </button>
          ))}
        </div>

        <h1 className="signin__title">
          {stage === 'start'
            ? t.titleStart
            : method === 'email'
              ? t.titleCheckEmail
              : t.titleCheckPhone}
        </h1>

        <p className="signin__lede">
          {stage === 'start' ? (
            method === 'email' ? t.ledeEmail : t.ledePhone
          ) : method === 'email' ? (
            <>
              {t.sentEmailBefore}
              <strong>{email}</strong>
              {t.sentEmailAfter}
            </>
          ) : (
            <>
              {t.sentPhoneBefore}
              <strong>{phone ? maskPhone(phone) : ''}</strong>
              {t.sentPhoneAfter}
            </>
          )}
        </p>

        {stage === 'start' && method === 'email' && (
          <label className="field">
            <span className="field__label">{t.emailLabel}</span>
            <input
              className="field__input"
              type="email"
              name="email"
              autoComplete="username"
              autoFocus
              required
              placeholder={t.emailPlaceholder}
              value={email}
              onChange={(ev) => setEmail(ev.target.value)}
              disabled={busy}
            />
          </label>
        )}

        {stage === 'start' && method === 'phone' && (
          <>
            <label className="field">
              <span className="field__label">{t.countryLabel}</span>
              <select
                className="field__input"
                name="dial"
                value={dial}
                onChange={(ev) => setDial(ev.target.value)}
                disabled={busy}
              >
                {DIAL_CODES.map((d) => (
                  <option key={d.country} value={d.dial}>
                    {dialLabel(d, lang)}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="field__label">{t.mobileLabel}</span>
              <input
                className="field__input"
                type="tel"
                name="phone"
                autoComplete="tel"
                inputMode="tel"
                autoFocus
                required
                placeholder="604 555 0123"
                value={phoneTyped}
                onChange={(ev) => setPhoneTyped(ev.target.value)}
                disabled={busy}
              />
            </label>
          </>
        )}

        {stage === 'code' && (
          <label className="field">
            <span className="field__label">{t.codeLabel}</span>
            <input
              ref={codeRef}
              className="field__input field__input--code"
              type="text"
              name="otp"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={6}
              required
              placeholder="000000"
              value={code}
              onChange={(ev) => setCode(ev.target.value.replace(/\D/g, ''))}
              disabled={busy}
            />
          </label>
        )}

        {error && (
          <p className="alert" role="alert">
            {/* Only the not-found refusal names support, so only it becomes a link. */}
            <SupportText
              text={error}
              href={supportMailto({ reason: 'not_found', lang, method, tried: method === 'email' ? email : phone })}
            />
          </p>
        )}

        <button
          className="btn btn--primary"
          type="submit"
          disabled={busy || (stage === 'start' ? !ready : code.length !== 6)}
        >
          {busy
            ? stage === 'start'
              ? t.sending
              : t.verifying
            : stage === 'start'
              ? method === 'email'
                ? t.emailMeACode
                : t.textMeACode
              : t.signIn}
        </button>

        {stage === 'start' && smsOffered === true && (
          <div className="signin__footer">
            <button
              type="button"
              className="btn btn--link"
              onClick={() => switchMethod(method === 'email' ? 'phone' : 'email')}
              disabled={busy}
            >
              {method === 'email' ? t.usePhoneInstead : t.useEmailInstead}
            </button>
          </div>
        )}

        {stage === 'code' && (
          <p className="signin__help">
            <SupportText
              text={method === 'email' ? t.noCodeEmail : t.noCodePhone}
              href={supportMailto({ reason: 'no_code', lang, method, tried: method === 'email' ? email : phone })}
            />
          </p>
        )}

        {stage === 'code' && (
          <div className="signin__footer">
            <button
              type="button"
              className="btn btn--link"
              onClick={() => sendCode()}
              disabled={busy || (resentAt !== null && Date.now() - resentAt < RESEND_COOLDOWN_MS)}
            >
              {t.sendAnother}
            </button>
            <button
              type="button"
              className="btn btn--link"
              onClick={() => {
                setStage('start')
                setCode('')
                setError(null)
              }}
            >
              {method === 'email' ? t.differentAddress : t.differentNumber}
            </button>
          </div>
        )}
      </form>

      <p className="signin__legal">
        {t.legal}
      </p>
    </main>
  )
}
